import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Container from '../../components/shared/Container';
import { useHeader } from '../../context/HeaderContext';
import { getLocations, submitContactForm } from '../../lib/sanity';
import SEO from '../../components/shared/SEO';
import StructuredData from '../../components/shared/StructuredData';
import { trackContactFormSubmit, trackPhoneClick } from '../../utils/gtm';
import { generateBreadcrumbSchema } from '../../utils/schema';
import { sortLocationsByDefault } from '../../utils/locationSorting';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

export default function ContactPage() {
  const { setUseBlurredBackground } = useHeader();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  // Enable blurred background for header
  useEffect(() => {
    setUseBlurredBackground(true);
    
    return () => {
      setUseBlurredBackground(false);
    };
  }, [setUseBlurredBackground]);

  // Fetch locations
  useEffect(() => {
    async function fetchLocations() {
      try {
        const fetchedLocations = await getLocations();
        const sortedData = sortLocationsByDefault(fetchedLocations || []);
        setLocations(sortedData);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchLocations();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      // Submit to Sanity
      const result = await submitContactForm(formData);
      
      if (result.success) {
        // Track form submission
        const selectedLocationName = formData.location 
          ? locations.find(loc => loc._id === formData.location)?.name 
          : null;
        trackContactFormSubmit(selectedLocationName);
        
        setFormStatus({
          type: 'success',
          message: 'Thank you for contacting us! We\'ll get back to you soon.'
        });
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          location: '',
          message: ''
        });
      } else {
        setFormStatus({
          type: 'error',
          message: 'Sorry, there was an error submitting your message. Please try again.'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Sorry, there was an error submitting your message. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-brand-cream min-h-screen">
      <SEO 
        title="Contact Us | The Original Pancake House - Get in Touch" 
        description="Contact The Original Pancake House. Find phone numbers and addresses for all our restaurant locations, or use our contact form to send us a message. We're here to help with questions, feedback, or special requests."
        canonicalUrl="/contact"
        openGraph={{
          image: "/oph-social-share4.jpg"
        }}
      />
      
      {/* Structured Data for SEO */}
      <StructuredData data={generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Contact', url: '/contact' }
      ])} />
      
      {/* Hero Section */}
      <motion.div 
        className="bg-brand-navy text-white pt-32 pb-12"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <Container>
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-heading mb-4">Contact Us</h1>
            <p className="text-xl text-white text-opacity-90 max-w-2xl mx-auto">
              Have a question or feedback? We'd love to hear from you. Fill out the form below or visit us at any of our locations.
            </p>
          </div>
        </Container>
      </motion.div>

      {/* Form Section */}
      <section className="py-16 bg-white">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-8">
              Send Us a Message
            </motion.h2>

            <motion.form variants={itemVariants} onSubmit={handleSubmit} className="space-y-6">
              {/* First Name and Last Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-brand-navy mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy"
                  />
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-brand-navy mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy"
                  />
                </div>
              </div>

              {/* Email and Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-brand-navy mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-brand-navy mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy"
                  />
                </div>
              </div>

              {/* Location Select */}
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-brand-navy mb-2">
                  Location
                </label>
                <select
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy bg-white"
                >
                  <option value="">Select a location (optional)</option>
                  {locations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-brand-navy mb-2">
                  How can we help? *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-navy resize-vertical"
                  placeholder="Tell us about your inquiry..."
                />
              </div>

              {/* Status Message */}
              {formStatus.message && (
                <div className={`p-4 rounded-lg ${
                  formStatus.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {formStatus.message}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-brand-navy text-white py-4 px-6 rounded-lg font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </motion.form>
          </motion.div>
        </Container>
      </section>

      {/* All Locations Section */}
      <section className="py-16 bg-brand-cream">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-12">
              Our Locations
            </motion.h2>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white p-6 rounded-lg shadow-sm animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : (
              <motion.div 
                variants={itemVariants}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {locations.map((location) => (
                  <div 
                    key={location._id} 
                    className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <h3 className="text-xl font-heading text-brand-navy mb-3">
                      {location.name}
                    </h3>
                    
                    {/* Address */}
                    {location.address && (
                      <div className="mb-3">
                        <div className="flex items-start">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-brand-navy mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <div className="text-gray-700 text-sm">
                            <p>{location.address.street}</p>
                            <p>{location.address.city}, {location.address.state} {location.address.zipCode}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Phone */}
                    {location.phoneNumber && (
                      <div className="mb-3">
                        <div className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-brand-navy mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <a 
                            href={`tel:${location.phoneNumber.replace(/\D/g, '')}`}
                            onClick={() => trackPhoneClick(location.name, location.phoneNumber, 'contact_page')}
                            className="text-brand-navy hover:text-brand-orange transition-colors text-sm"
                          >
                            {location.phoneNumber}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* View Details Link */}
                    <a
                      href={`/locations/${location.slug}`}
                      className="inline-flex items-center text-brand-navy hover:text-brand-orange transition-colors text-sm font-medium mt-2"
                    >
                      View Details
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  </div>
                ))}
              </motion.div>
            )}
          </motion.div>
        </Container>
      </section>
    </div>
  );
}

